<script setup lang="ts">
import { computed, ref } from 'vue'
import { Head, router } from '@inertiajs/vue3'
import { useForm } from '@inertiajs/vue3'
import PageHeading from '@/Components/Headings/PageHeading.vue'
import { PageHeading as PageHeadingTS } from '@/types/PageHeading'
import Button from '@/Components/Utils/Button.vue'
import { useFormatCurrency } from '@/Composables/useFormatCurrency'
import { InputNumber, Select } from 'primevue'

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faBoxOpen, faPrint } from '@fal'
import { library } from '@fortawesome/fontawesome-svg-core'
library.add(faBoxOpen, faPrint)

const props = defineProps<{
    title?: string
    pageHead?: PageHeadingTS
    order: {
        id: number
        slug: string
        reference: string
        type: 'seng' | 'flexo'
        customer_id: number | null
        product_id: number | null
        quantity: number
        dimension_width: number | null
        dimension_height: number | null
        item_position: string | null
        file_merek: string | null
        note: string | null
        is_locked: boolean
    }
    seng_products: {
        id: number
        name: string
        code: string
        price: number
        is_pinned: boolean
    }[]
    customers: {
        id: number
        name: string
    }[]
    flexo_product: {
        id: number
        name: string
        price: number
    } | null
    flexo_customer_prices: Record<number, number>
}>()

const form = useForm({
    reference: props.order.reference,
    customer_id: props.order.customer_id,
    product_id: props.order.product_id,
    quantity: props.order.quantity,
    dimension_width: props.order.dimension_width,
    dimension_height: props.order.dimension_height,
    item_position: props.order.item_position ?? '',
    file_merek: props.order.file_merek ?? '',
    note: props.order.note ?? '',
})

const isFlexo = computed(() => props.order.type === 'flexo')
const isLocked = computed(() => props.order.is_locked)

const flexoUnitPrice = computed(() => {
    if (!props.flexo_product) return 0
    if (form.customer_id && props.flexo_customer_prices[form.customer_id]) {
        return props.flexo_customer_prices[form.customer_id]
    }
    return props.flexo_product.price
})

const flexoTotal = computed(() => {
    if (!form.dimension_width || !form.dimension_height || !form.quantity) return 0
    return form.quantity * form.dimension_width * form.dimension_height * flexoUnitPrice.value
})

const sengTotal = computed(() => {
    if (!form.product_id) return 0
    const product = props.seng_products.find(p => p.id === form.product_id)
    if (!product) return 0
    return form.quantity * product.price
})

const selectedSengProduct = computed(() =>
    props.seng_products.find(p => p.id === form.product_id) ?? null
)

const onSubmit = () => {
    form.patch(route('casheer.orders.update', props.order.id))
}
</script>

<template>
    <Head :title="title" />
    <PageHeading :data="pageHead" />

    <div class="px-4 sm:px-6 lg:px-8 mt-6 max-w-2xl">

        <!-- Type badge (readonly) -->
        <div class="mb-6">
            <span
                class="inline-flex items-center gap-2 rounded-xl border-2 px-4 py-2 font-semibold text-sm"
                :class="isFlexo
                    ? 'border-blue-400 bg-blue-50 text-blue-700'
                    : 'border-amber-400 bg-amber-50 text-amber-700'"
            >
                <FontAwesomeIcon :icon="isFlexo ? 'fal fa-print' : 'fal fa-box-open'" fixed-width aria-hidden="true" />
                {{ isFlexo ? 'Flexo' : 'Seng' }}
            </span>
            <p class="text-xs text-gray-400 mt-1">Jenis produk tidak bisa diubah setelah order dibuat.</p>
        </div>

        <div class="space-y-5">

            <!-- Locked notice -->
            <div
                v-if="isLocked"
                class="flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
            >
                <span class="mt-0.5">⚠️</span>
                <div>
                    <p class="font-semibold">Produksi order ini sudah selesai</p>
                    <p class="mt-0.5 text-amber-700">
                        Pelanggan, produk, jumlah{{ isFlexo ? ', dan dimensi' : '' }} tidak bisa diubah lagi.
                        Anda masih bisa mengubah posisi barang, file merek, dan catatan.
                    </p>
                </div>
            </div>

            <!-- No. Order -->
            <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Nomor Order</label>
                <input
                    v-model="form.reference"
                    type="text"
                    class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
                <p v-if="form.errors.reference" class="mt-1 text-sm text-red-500">{{ form.errors.reference }}</p>
            </div>

            <!-- Pelanggan -->
            <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Pelanggan</label>
                <Select
                    v-model="form.customer_id"
                    :options="customers"
                    optionLabel="name"
                    optionValue="id"
                    placeholder="Pilih pelanggan..."
                    filter
                    fluid
                    :disabled="isLocked"
                    :invalid="!!form.errors.customer_id"
                />
                <p v-if="form.errors.customer_id" class="mt-1 text-sm text-red-500">{{ form.errors.customer_id }}</p>
            </div>

            <!-- SENG: Pilih produk -->
            <template v-if="!isFlexo">
                <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">Produk Seng <span class="text-red-500">*</span></label>
                    <Select
                        v-model="form.product_id"
                        :options="seng_products"
                        optionLabel="name"
                        optionValue="id"
                        placeholder="Pilih produk Seng..."
                        filter
                        fluid
                        :disabled="isLocked"
                        :invalid="!!form.errors.product_id"
                    >
                        <template #option="{ option }">
                            <div class="flex justify-between w-full gap-4">
                                <span>{{ option.name }}</span>
                                <span class="text-gray-400 text-sm">{{ useFormatCurrency(option.price) }}</span>
                            </div>
                        </template>
                    </Select>
                    <p v-if="form.errors.product_id" class="mt-1 text-sm text-red-500">{{ form.errors.product_id }}</p>
                </div>
            </template>

            <!-- FLEXO: Dimensi -->
            <template v-if="isFlexo">
                <div class="grid grid-cols-2 gap-4">
                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-1">Panjang (cm) <span class="text-red-500">*</span></label>
                        <InputNumber
                            v-model="form.dimension_height"
                            placeholder="cm"
                            :minFractionDigits="0"
                            :maxFractionDigits="2"
                            :min="0.1"
                            fluid
                            :disabled="isLocked"
                            :invalid="!!form.errors.dimension_height"
                        />
                        <p v-if="form.errors.dimension_height" class="mt-1 text-sm text-red-500">{{ form.errors.dimension_height }}</p>
                    </div>
                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-1">Lebar (cm) <span class="text-red-500">*</span></label>
                        <InputNumber
                            v-model="form.dimension_width"
                            placeholder="cm"
                            :minFractionDigits="0"
                            :maxFractionDigits="2"
                            :min="0.1"
                            fluid
                            :disabled="isLocked"
                            :invalid="!!form.errors.dimension_width"
                        />
                        <p v-if="form.errors.dimension_width" class="mt-1 text-sm text-red-500">{{ form.errors.dimension_width }}</p>
                    </div>
                </div>
                <div class="text-sm text-gray-500">
                    Harga per unit:
                    <span class="font-semibold text-gray-700">{{ useFormatCurrency(flexoUnitPrice) }}/cm²</span>
                    <span v-if="form.customer_id && flexo_customer_prices[form.customer_id]" class="ml-1 text-blue-600 text-xs">(harga khusus)</span>
                </div>
            </template>

            <!-- Jumlah -->
            <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Jumlah <span class="text-red-500">*</span></label>
                <InputNumber
                    v-model="form.quantity"
                    :min="1"
                    :max="999"
                    showButtons
                    fluid
                    :disabled="isLocked"
                    :invalid="!!form.errors.quantity"
                />
                <p v-if="form.errors.quantity" class="mt-1 text-sm text-red-500">{{ form.errors.quantity }}</p>
            </div>

            <!-- Posisi Barang -->
            <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Posisi Barang</label>
                <input
                    v-model="form.item_position"
                    type="text"
                    placeholder="Lokasi barang setelah produksi..."
                    class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
                <p v-if="form.errors.item_position" class="mt-1 text-sm text-red-500">{{ form.errors.item_position }}</p>
            </div>

            <!-- Merek -->
            <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">File Merek</label>
                <input
                    v-model="form.file_merek"
                    type="text"
                    placeholder="File Merek produk..."
                    class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
                <p v-if="form.errors.file_merek" class="mt-1 text-sm text-red-500">{{ form.errors.file_merek }}</p>
            </div>

            <!-- Catatan -->
            <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Catatan</label>
                <textarea
                    v-model="form.note"
                    rows="3"
                    placeholder="Catatan tambahan..."
                    class="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
                <p v-if="form.errors.note" class="mt-1 text-sm text-red-500">{{ form.errors.note }}</p>
            </div>

            <!-- Preview total -->
            <div class="rounded-xl border border-gray-200 bg-gray-50 px-5 py-4">
                <div v-if="isFlexo" class="text-sm text-gray-500 mb-1">
                    {{ form.quantity }} pcs × {{ form.dimension_height || '?' }} cm × {{ form.dimension_width || '?' }} cm × {{ useFormatCurrency(flexoUnitPrice) }}
                </div>
                <div v-else-if="selectedSengProduct" class="text-sm text-gray-500 mb-1">
                    {{ form.quantity }} pcs × {{ useFormatCurrency(selectedSengProduct.price) }}
                </div>
                <div class="text-lg font-bold text-gray-800">
                    Total: {{ useFormatCurrency(isFlexo ? flexoTotal : sengTotal) }}
                </div>
            </div>

            <!-- Submit -->
            <Button
                label="Simpan Perubahan"
                type="save"
                full
                :loading="form.processing"
                :disabled="form.processing || !form.isDirty"
                @click="onSubmit"
            />
        </div>
    </div>
</template>
